import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import Lenis from "lenis";

interface MotionRailContextValue {
  /** 0..1 page-scroll progress */
  progress: number;
  /** instantaneous scroll velocity in px/ms */
  velocity: number;
  /** current chapter id (last seen), set by ChapterMarker */
  chapter: string;
  setChapter: (id: string) => void;
  /** prefers-reduced-motion */
  reduced: boolean;
  /** the underlying Lenis instance, or null if reduced-motion disabled it */
  lenis: Lenis | null;
}

const noop = () => {};

const MotionRailContext = createContext<MotionRailContextValue>({
  progress: 0,
  velocity: 0,
  chapter: "",
  setChapter: noop,
  reduced: false,
  lenis: null,
});

export const useMotionRail = () => useContext(MotionRailContext);

/**
 * Top-level scroll + motion brain. Owns one Lenis instance, broadcasts
 * progress/velocity/chapter to anything that needs it. Honors
 * prefers-reduced-motion (static native scroll, zero velocity).
 */
export const MotionRailProvider = ({ children }: { children: ReactNode }) => {
  const [progress, setProgress] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const [chapter, setChapter] = useState("");
  const lenisRef = useRef<Lenis | null>(null);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => setReduced(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (reduced) return; // native scroll, no Lenis

    const lenis = new Lenis({
      duration: 1.0,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let raf = 0;
    const tick = (time: number) => {
      lenis.raf(time);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    lenis.on("scroll", ({ progress: p, velocity: v }: { progress: number; velocity: number }) => {
      setProgress(p || 0);
      setVelocity(Math.abs(v || 0));
    });

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reduced]);

  // Native-scroll fallback for reduced-motion: still emit progress so the
  // ScrollRail keeps working.
  useEffect(() => {
    if (!reduced) return;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? window.scrollY / max : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [reduced]);

  return (
    <MotionRailContext.Provider
      value={{ progress, velocity, chapter, setChapter, reduced, lenis: lenisRef.current }}
    >
      {children}
    </MotionRailContext.Provider>
  );
};
